/**
 * Change Deal Stage Server Function
 *
 * Updates a deal's stage via API.
 */

import { createServerFn } from "@tanstack/react-start";
import { changeDealStageApiWorkspacesWorkspaceIdDealsDealIdStagePost } from "@/lib/api/generated/deals/deals";
import type { DealStage } from "@/lib/api/generated/dealStage";
import { extractApiErrorMessage } from "@/lib/api/error";
import { logError, logInfo } from "@/lib/structured-log";

export const changeDealStageServer = createServerFn({ method: "POST" })
  .validator(
    (data: { workspaceId: string; dealId: string; newStage: DealStage; reason?: string }) => data,
  )
  .handler(async ({ data }) => {
    const { workspaceId, dealId, newStage, reason } = data;

    logInfo("deals.stage.change.started", {
      workspaceId,
      dealId,
      newStage,
    });

    try {
      const response = await changeDealStageApiWorkspacesWorkspaceIdDealsDealIdStagePost(
        workspaceId,
        dealId,
        {
          new_stage: newStage,
          reason,
        },
      );

      const status = (response as { status: number }).status;

      if (status !== 200) {
        if (status === 402) {
          throw new Error("BILLING_INSUFFICIENT_CREDITS");
        }
        const errorMessage = extractApiErrorMessage(response, "Failed to change deal stage");
        logError("deals.stage.change.failed", { error: errorMessage });
        throw new Error(errorMessage);
      }

      logInfo("deals.stage.change.completed", { dealId, newStage });

      return response.data;
    } catch (error) {
      const errorMessage = extractApiErrorMessage(
        error,
        "An error occurred while changing deal stage",
      );
      logError("deals.stage.change.error", { error: errorMessage });
      throw new Error(errorMessage);
    }
  });
